import { usePriceFeed } from 'hooks/usePriceFeed'
import { PnLThresholdsCSS } from './PnLThresholdsCSS.styled'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { LONG_DASH } from 'constants'
import { currencyFormatter } from 'utils'
import { useLvgState } from 'hooks/games/lvg/useLvgState'
import { getPnL } from 'utils/pnl'
import { onlyDigits } from 'utils/uiUtils_TS'
import { StateSlider } from './StateSlider'
import { TrailingInputs } from './TrailingInputs'
import { ContentSwitcherByState } from 'components/Atoms/ContentSwitcherByState'

const STOP_TYPES = [
  { name: 'FIXED', value: 'fixed' },
  { name: 'TRAILING', value: 'trailing' },
  // { name: 'Both', value: 'both' },
]

export const PnLThreshold = ({ game, newOrderParams, onToggle, onParamChange, onError }) => {
  const { priceFeed } = usePriceFeed(newOrderParams.asset)
  const { activeAsset } = useLvgState()

  const [visible, setVisible] = useState(true)

  const [profitPercentage, setProfitPercentage] = useState()
  const [lossPercentage, setLossPercentage] = useState()
  const [lossFocused, setLossFocused] = useState()
  const [profitFocused, setProfitFocused] = useState()

  const [sliderSelection, setSliderType] = useState(STOP_TYPES.at(0))

  const setSliderSelection = (stopType) => {
    if (stopType.value === 'trailing') 
      onParamChange({ userStopLossPrice: '', userStopLossValue: '' })
    
    if (stopType.value === 'fixed') 
      onParamChange({ trailingStopValue: '', trailingStopPercent: '' })
    
    setSliderType(stopType)
  }

  const toggleVisible = () => {
    setVisible(!visible)
    onToggle && onToggle()
  }

  //Profit
  const profit = useMemo(() => {
    if (profitFocused) return ''
    if (!newOrderParams.takeProfitPrice) return LONG_DASH
    const currentPrice = priceFeed?.price
    const fillQuantity = (newOrderParams.amount * newOrderParams.leverage) / currentPrice
    const pnl = getPnL(
      currentPrice,
      newOrderParams.takeProfitPrice,
      newOrderParams.side,
      fillQuantity,
      newOrderParams.leverage,
      newOrderParams.asset
    )

    if (pnl < 0) return LONG_DASH

    return currencyFormatter(pnl, {
      noCurrencySign: true,
      decimalDigits: activeAsset?.fiatDecimals,
    })
  }, [
    priceFeed?.price,
    activeAsset?.fiatDecimals,
    newOrderParams?.amount,
    newOrderParams?.leverage,
    newOrderParams?.takeProfitPrice,
    newOrderParams?.side,
    newOrderParams?.asset,
    profitFocused,
  ])

  const getTakeProfitPriceFromValue = useMemo(() => {
    if (!profitFocused) return ''
    const takeProfitValue = newOrderParams?.takeProfitValue || 0
    const entryPrice = priceFeed?.price
    const side = newOrderParams.side
    const quantity = (newOrderParams.amount * newOrderParams.leverage) / priceFeed.price
    let takeProfitPrice
    const feeRate = 0.23

    if (side === 'buy') takeProfitPrice = entryPrice + takeProfitValue / (quantity * (1 - feeRate))
    else takeProfitPrice = entryPrice - takeProfitValue / (quantity * (1 - feeRate))

    return currencyFormatter(takeProfitPrice, {
      noCurrencySign: false,
      decimalDigits: activeAsset?.fiatDecimals,
    })
  }, [
    newOrderParams?.amount,
    newOrderParams?.takeProfitValue,
    priceFeed?.price,
    newOrderParams?.side,
    newOrderParams?.leverage,
    activeAsset?.fiatDecimals,
    profitFocused,
  ])

  // Loss
  const onChangeLoss = (e) => {
    let value = onlyDigits(e.target.value)
    if (Number(value) > newOrderParams.amount) value = newOrderParams.amount.toString()
    onParamChange({ userStopLossPrice: '', userStopLossValue: value })
  }

  const loss = useMemo(() => {
    if (lossFocused) return ''
    if (!newOrderParams.userStopLossPrice) return LONG_DASH
    const currentPrice = priceFeed?.price
    const fillQuantity = (newOrderParams.amount * newOrderParams.leverage) / currentPrice
    const pnl = getPnL(
      currentPrice,
      newOrderParams.userStopLossPrice,
      newOrderParams.side,
      fillQuantity,
      newOrderParams.leverage,
      newOrderParams.asset
    )

    if (pnl > 0) return LONG_DASH

    return currencyFormatter(Math.abs(pnl), {
      noCurrencySign: true,
      decimalDigits: activeAsset?.fiatDecimals,
    })
  }, [
    priceFeed?.price,
    activeAsset?.fiatDecimals,
    newOrderParams?.amount,
    newOrderParams?.leverage,
    newOrderParams?.userStopLossPrice,
    newOrderParams?.side,
    newOrderParams?.asset,
    lossFocused,
  ])

  const getStopLossPriceFromValue = useMemo(() => {
    if (!lossFocused) return ''
    const stopLossValue = Math.abs(newOrderParams.userStopLossValue)
    const entryPrice = priceFeed.price
    const side = newOrderParams.side
    const quantity = (newOrderParams.amount * newOrderParams.leverage) / priceFeed.price
    let stopLossPrice
    if (side === 'buy') stopLossPrice = entryPrice - stopLossValue / quantity
    else stopLossPrice = entryPrice + stopLossValue / quantity

    return currencyFormatter(stopLossPrice, {
      noCurrencySign: false,
      decimalDigits: activeAsset?.fiatDecimals,
    })
  }, [
    newOrderParams?.amount,
    newOrderParams?.userStopLossValue,
    priceFeed?.price,
    newOrderParams?.side,
    newOrderParams?.leverage,
    activeAsset?.fiatDecimals,
    lossFocused,
  ])

  const checkTakeProfitError = useCallback(() => {
    const currentPrice = priceFeed?.price
    if (newOrderParams.side === 'buy' && Number(newOrderParams.takeProfitPrice) < Number(currentPrice)) {
      onError(
        `The new value should be above ${currencyFormatter(currentPrice, {
          noCurrencySign: false,
          decimalDigits: activeAsset?.fiatDecimals,
        })}`
      )
    }

    if (newOrderParams.side === 'sell' && Number(newOrderParams.takeProfitPrice) > Number(currentPrice)) {
      onError(
        `The new value should be below ${currencyFormatter(currentPrice, {
          noCurrencySign: false,
          decimalDigits: activeAsset?.fiatDecimals,
        })}`
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAsset?.fiatDecimals, priceFeed?.price, newOrderParams?.side, newOrderParams?.takeProfitPrice])

  const checkStopLossError = useCallback(() => {
    const currentPrice = priceFeed?.price

    if (newOrderParams.side === 'buy' && Number(newOrderParams.userStopLossPrice) > Number(currentPrice)) {
      onError(
        `The new value should be below ${currencyFormatter(currentPrice, {
          noCurrencySign: false,
          decimalDigits: activeAsset.fiatDecimals,
        })}`
      )
    }

    if (newOrderParams.side === 'sell' && Number(newOrderParams.userStopLossPrice) < Number(currentPrice)) {
      onError(
        `The new value should be above ${currencyFormatter(currentPrice, {
          noCurrencySign: false,
          decimalDigits: activeAsset.fiatDecimals,
        })}`
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAsset?.fiatDecimals, priceFeed?.price, newOrderParams?.side, newOrderParams.userStopLossPrice])

  const updateProfitPercentage = (event) => {
    if (!event) {
      setProfitPercentage()
      return
    }
    const { value } = event.target.dataset

    const tpslValues = {
      takeProfitValue: '',
      takeProfitPrice: '',
    }
    if (Number(value) === profitPercentage) {
      onParamChange(tpslValues)
      setProfitPercentage()
    } else {
      if (newOrderParams.side === 'buy')
        tpslValues.takeProfitPrice = Number((priceFeed?.price * (1 + Number(value))).toFixed(3))
      else tpslValues.takeProfitPrice = Number((priceFeed?.price * (1 - Number(value))).toFixed(3))

      onParamChange(tpslValues)
      setProfitPercentage(Number(value))
    }
    setProfitFocused(false)
  }
  const updateLossPercentage = (event) => {
    if (!event) {
      setProfitPercentage()
      return
    }
    
    const { value } = event.target.dataset
    const tpslValues = {
      userStopLossValue: '',
      userStopLossPrice: '',
    }
    if (Number(value) === lossPercentage) {
      onParamChange(tpslValues)
      setLossPercentage()
    } else {
      if (newOrderParams.side === 'buy')
        tpslValues.userStopLossPrice = Number((priceFeed?.price * (1 + Number(value))).toFixed(3))
      else tpslValues.userStopLossPrice = Number((priceFeed?.price * (1 - Number(value))).toFixed(3))

      onParamChange(tpslValues)
      setLossPercentage(Number(value))
    }
    setLossFocused(false)
  }

  const onTrailingChange = (trailingValues) => {
    onParamChange(trailingValues)
  }

  return (
    <PnLThresholdsCSS>
      <div className='row pl_max-min_option'>
        <div className='subtitle'>Take Profit / Stop Loss</div>
        <div className='switch_wrapper'>
          <input
            type='checkbox'
            className='toggle_switch-checkbox'
            id={`tp_sl_toggle-switch${game}`}
            checked={visible}
            readOnly
          />
          <label htmlFor={`tp_sl_toggle-switch${game}`} className='toggle_switch-label' onClick={toggleVisible}></label>
          <div className='show_maxmin'>
            <div className='max'>
              <fieldset>
                <strong>{`Close bid at ${newOrderParams.side === 'buy' ? 'max' : 'min'} price`}</strong>
                <input
                  type='text'
                  placeholder={`${newOrderParams.side === 'buy' ? 'Max' : 'Min'} Price`}
                  value={
                    newOrderParams.takeProfitPrice === '' ? getTakeProfitPriceFromValue : newOrderParams.takeProfitPrice
                  }
                  onChange={(e) => onParamChange({ takeProfitPrice: onlyDigits(e.target.value), takeProfitValue: '' })}
                  onBlur={checkTakeProfitError}
                  onClick={() => {
                    setProfitPercentage()
                    setProfitFocused(false)
                    onParamChange({ takeProfitValue: '' })
                  }}
                />
                <div className='percent_value_buttons'>
                  <button
                    className={profitPercentage === 0.05 ? 'profit_active' : 'profit'}
                    data-value='0.05'
                    onClick={updateProfitPercentage}
                  >
                    +5%
                  </button>
                  <button
                    className={profitPercentage === 0.1 ? 'profit_active' : 'profit'}
                    data-value='0.1'
                    onClick={updateProfitPercentage}
                  >
                    +10%
                  </button>
                  <button
                    className={profitPercentage === 0.25 ? 'profit_active' : 'profit'}
                    data-value='0.25'
                    onClick={updateProfitPercentage}
                  >
                    +25%
                  </button>
                  <button
                    className={profitPercentage === 0.5 ? 'profit_active' : 'profit'}
                    data-value='0.5'
                    onClick={updateProfitPercentage}
                  >
                    +50%
                  </button>
                  <button
                    className={profitPercentage === 1 ? 'profit_active' : 'profit'}
                    data-value='1'
                    onClick={updateProfitPercentage}
                  >
                    +100%
                  </button>
                </div>
              </fieldset>
              <fieldset>
                <strong>Profit</strong>
                <div className={`input_wrapper profit ${profit === LONG_DASH ? 'noContent' : ''}`}>
                  <input
                    type='text'
                    className='tpsl_result_data profit'
                    value={newOrderParams.takeProfitValue === '' ? profit : newOrderParams.takeProfitValue}
                    onChange={(e) => {
                      onParamChange({ takeProfitPrice: '', takeProfitValue: onlyDigits(e.target.value) })
                    }}
                    onClick={() => {
                      setProfitPercentage()
                      setProfitFocused(true)
                      onParamChange({ takeProfitPrice: '' })
                    }}
                  />
                </div>
              </fieldset>
            </div>
            <StateSlider className='secondary_slider' stateOptions={STOP_TYPES} title={<>SET <strong>FIXED STOP</strong> OR <strong>TRAILING STOP</strong></>} onSelection={setSliderSelection} />
              <div className='min'>
              <ContentSwitcherByState noWrapper activeState={sliderSelection.value} stateObject={{
                'fixed': (
                    <>
                      <fieldset>
                  <strong>{`Close bid at ${newOrderParams.side === 'buy' ? 'min' : 'max'} price`}</strong>
                  <input
                    type='text'
                    placeholder={`${newOrderParams.side === 'buy' ? 'Min' : 'Max'} Price`}
                    value={
                      newOrderParams.userStopLossPrice === ''
                        ? getStopLossPriceFromValue
                        : newOrderParams.userStopLossPrice
                    }
                    onChange={(e) => onParamChange({ userStopLossPrice: onlyDigits(e.target.value) })}
                    onBlur={checkStopLossError}
                    onClick={() => {
                      setLossFocused(false)
                      setLossPercentage()
                      onParamChange({ takeProfitValue: '' })
                    }}
                  />
                  <div className='percent_value_buttons'>
                    <button
                      className={lossPercentage === -0.05 ? 'loss_active' : 'loss'}
                      data-value='-0.05'
                      onClick={updateLossPercentage}
                    >
                      -5%
                    </button>
                    <button
                      className={lossPercentage === -0.1 ? 'loss_active' : 'loss'}
                      data-value='-0.1'
                      onClick={updateLossPercentage}
                    >
                      -10%
                    </button>
                    <button
                      className={lossPercentage === -0.25 ? 'loss_active' : 'loss'}
                      data-value='-0.25'
                      onClick={updateLossPercentage}
                    >
                      -25%
                    </button>
                    <button
                      className={lossPercentage === -0.5 ? 'loss_active' : 'loss'}
                      data-value='-0.5'
                      onClick={updateLossPercentage}
                    >
                      -50%
                    </button>
                    <button
                      className={lossPercentage === -1 ? 'loss_active' : 'loss'}
                      data-value='-1'
                      onClick={updateLossPercentage}
                    >
                      -100%
                    </button>
                  </div>
                </fieldset>
                <fieldset>
                  <strong>Loss</strong>
                  <div className={`input_wrapper loss ${loss === LONG_DASH ? 'noContent' : ''}`}>
                    <input
                      type='text'
                      className='tpsl_result_data loss'
                      value={newOrderParams.userStopLossValue === '' ? loss : newOrderParams.userStopLossValue}
                      onChange={onChangeLoss}
                      onClick={() => {
                        setLossPercentage()
                        setLossFocused(true)
                        onParamChange({ userStopLossPrice: '' })
                      }}
                    />
                  </div>
                    </fieldset></>),
                'trailing': <TrailingInputs onChange={onTrailingChange} reset={!visible} />,
              }} />
              </div>
          </div>
        </div>
      </div>
    </PnLThresholdsCSS>
  )
}
